import { clearAll, getJwt, refresh } from '@/lib/auth';

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';
}

async function doFetch(path: string, init: RequestInit): Promise<Response> {
  const headers = new Headers(init.headers ?? {});
  const jwt = getJwt();
  if (jwt && !headers.has('authorization')) {
    headers.set('authorization', `Bearer ${jwt}`);
  }
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  return fetch(`${apiBase()}${path}`, { ...init, headers });
}

export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  let res = await doFetch(path, init);
  if (res.status === 401) {
    try {
      await refresh();
      res = await doFetch(path, init);
    } catch {
      await clearAll();
      if (typeof window !== 'undefined') {
        const hash = window.location.hash;
        if (!hash.startsWith('#/login')) {
          window.location.hash = '#/login';
        }
      }
      throw new ApiError(401, 'Unauthorized');
    }
  }
  if (!res.ok) {
    let code: string | undefined;
    let message = `Request failed: ${res.status}`;
    const ct = res.headers.get('content-type') ?? '';
    if (ct.includes('application/json')) {
      try {
        const j = (await res.json()) as { message?: string; code?: string };
        if (j.message) message = j.message;
        code = j.code;
      } catch {
        // ignore
      }
    }
    throw new ApiError(res.status, message, code);
  }
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    return (await res.json()) as T;
  }
  return undefined as T;
}
